import React, { FC, useCallback, useMemo } from 'react';
import Link from 'next/link';

import { BaseButton, btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import WebpImage from '@r1-frontend/ui-react/components/WebpImage';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { useWebp } from '@r1-frontend/shared/hooks/useWebp';

import { TStyles } from '~/src/features/movix-new/ContentCard';
import { useProgressiveImageWithFallbackDetection } from '~/src/helpers/hooks/useProgressiveImage';

import * as ST from './styled';

export enum EClickType {
    ON_BANNER = 'clickOnBanner',
    ON_BTN = 'clickOnBtn',
}

interface IBannerInfo {
    tab_title?: string,
    title?: string,
    tab_text?: string,
    link?: string,
    size?: string,
    image_background?: string,
    image_background_webp?: string,
    image_background_mobile?: string,
    image_background_mobile_webp?: string,
    image?: string,
    image_webp?: string,
    image_position?: string,
    image_mobile?: string,
    text_color?: string,
    subTitle?: string,
    text?: string,
    button_text?: string,
    phone_introduction?: number,
}

interface ILink {
    as?: string,
    href?: string,
}

export interface IBannerCustomStyles {
    wrapper?: TStyles,
    container?: TStyles,
    image?: TStyles,
    content?: TStyles,
}

interface IClickHandler {
    banner: IBannerInfo,
    bannerIdx: string | number,
    clickType: EClickType,
}

interface IBannerProps {
    onClickHandler: (props: IClickHandler) => void,
    titleButton?: string,
    image?: string,
    imageHeight?: string,
    title?: string,
    description?: string,
    background?: string,
    textColor?: string,
    typeButton?: string,
    dataTestForButton?: string,
    backgroundColor?: string,
    marginTop?: string,
    marginBottom?: string,
    marginRight?: string,
    banner: IBannerInfo,
    subTitle?: string,
    adaptive?: boolean,
    fixedHeight?: boolean,
    routeLink?: ILink,
    customStyles?: Record<string, any>,
    onInputChange?: (v: string) => void,
    onClickConnect?: () => void,
    beforeClick?: (bannerIdx: number | string | undefined) => void,
    bannerIdx?: string | number,
    withContainer?: boolean,
    customCSSStyles?: IBannerCustomStyles,

    [key: string]: any,
}

const Banner: FC<IBannerProps> = ({
    onClickHandler,
    titleButton,
    image,
    imageHeight,
    title,
    description,
    background,
    textColor,
    typeButton = 'primary',
    dataTestForButton,
    backgroundColor,
    marginTop,
    marginBottom,
    marginRight,
    banner = {},
    subTitle = '',
    adaptive = true,
    fixedHeight = false,
    routeLink = {},
    customStyles,
    onInputChange = () => ({}),
    onClickConnect = () => ({}),
    beforeClick = () => ({}),
    bannerIdx = '0',
    withContainer,
    customCSSStyles,
    ...props
}) => {
    const {
        frontImage,
        bgImage,
        bgImageMobile,
        frontImagePosition,
        buttonText,
        phoneIntroduction,
        descriptionText,
        timeout,
        subTitleText,
        titleText,
        txtColor,
        image_mobile,
        image_background_mobile_webp,
        image_background_webp,
        size,
        image_webp,
    } = useMemo(() => {
            const {
                image: bannerImage,
                image_webp,
                image_mobile,
                image_background,
                image_background_webp,
                image_background_mobile,
                image_background_mobile_webp,
                size,
            } = banner;

            const frontImagePosition = banner.image_position;
            const txtColor = textColor || banner.text_color;
            const titleText = title || banner.title;
            const subTitleText = subTitle || banner.subTitle;
            const descriptionText = description || banner.text;
            const buttonText = titleButton || banner.button_text;
            const phoneIntroduction = banner.phone_introduction ? banner.phone_introduction : 0;

            const timeout = { hasTimeout: !!bannerIdx };

            const frontImage = image || bannerImage;
            const bgImage = background || image_background;
            const bgImageMobile = image_background_mobile;

            return {
                frontImage,
                bgImage,
                bgImageMobile,
                frontImagePosition,
                buttonText,
                phoneIntroduction,
                descriptionText,
                timeout,
                subTitleText,
                titleText,
                txtColor,
                image_mobile,
                image_background_mobile_webp,
                image_background_webp,
                size,
                image_webp,
            };
        }, [background, banner, bannerIdx, description, image, subTitle, textColor, title, titleButton]);

    const extStyles = useMemo(() => ({
        content: {
            default: {
                padding: phoneIntroduction ? '10 0 30px 30px' : '70px 0 70px 35px',
            },
            desktop: {
                padding: phoneIntroduction ? '10 0 30px 30px' : '60px 0 70px 35px',
            },
            xsDesktop: {
                width: phoneIntroduction ? '550px' : '500px',
                'padding-top': phoneIntroduction ? '20px' : '60px',
            },
        },
        title: {
            desktop: {
                'margin-bottom': phoneIntroduction ? '10px' : '15px',
            },
        },
        phoneBlock: {
            input: {
                'color': txtColor || COLORS.TextPrimary,
                '&:focus': {
                    'border-color': txtColor || COLORS.TextPrimary,
                },
            },
            span: {
                'color': txtColor || COLORS.TextPrimary,
            },
            detailsLink: {
                'color': txtColor || COLORS.TextPrimary,
                'border-bottom': `1px solid ${txtColor || COLORS.TextPrimary}`,
            },
            offer: {
                'color': txtColor || COLORS.TextPrimary,
            },
        },
    }), [phoneIntroduction, txtColor]);

    const { sourceLoaded: frontImageMobile, isFallback: frontImageMobileIsFallback } =
        useProgressiveImageWithFallbackDetection(image_mobile, { timeout });
    const isWebpUsed = useWebp();

    const { sourceLoaded: frontImageWebp, isFallback: frontImageWebpIsFallback } =
        useProgressiveImageWithFallbackDetection(image_webp);
    const { sourceLoaded: bgImageMobileWebp, isFallback: bgImageMobileWebpIsFallback } =
        useProgressiveImageWithFallbackDetection(image_background_mobile_webp, { timeout });
    const { sourceLoaded: bgImageWebp, isFallback: bgImageWebpIsFallback } =
        useProgressiveImageWithFallbackDetection(background || image_background_webp, { timeout });

    const resultBgImage = (bgImageWebpIsFallback || !isWebpUsed) ? bgImage : bgImageWebp;
    const resultMobileImage = (bgImageMobileWebpIsFallback || !isWebpUsed) ? bgImageMobile : bgImageMobileWebp;
    const resultFontImage = (frontImageWebpIsFallback || !isWebpUsed) ? frontImage : frontImageWebp;

    const onBannerClick = useCallback(() => {
        onClickHandler({ banner, bannerIdx, clickType: EClickType.ON_BANNER });
    }, [onClickHandler, banner, bannerIdx]);

    const onBtnClick = useCallback((event) => {
        event.stopPropagation();
        onClickHandler({ banner, bannerIdx, clickType: EClickType.ON_BTN });
    }, [onClickHandler, banner, bannerIdx]);

    const content = useMemo(() => (
        <>
            <ST.Content
                adaptive={adaptive}
                size={size}
                customStyles={customStyles?.content}
                extStyles={extStyles?.content}
                styles={customCSSStyles?.content}
            >
                {subTitleText && <ST.SubTitle>{subTitleText}</ST.SubTitle>}
                <ST.Title
                    textColor={txtColor}
                    itemProp='name'
                    // @ts-ignore
                    dangerouslySetInnerHTML={{ __html: titleText }}
                    extStyles={extStyles?.title}
                />
                {descriptionText && (
                    <ST.Description
                        textColor={txtColor}
                        itemProp='description'
                        dangerouslySetInnerHTML={{ __html: descriptionText }}
                    />
                )}
                {
                    phoneIntroduction
                        ? <ST.PhoneBlock>
                            <ST.InputPhone extStyles={extStyles?.phoneBlock}
                                           textColor={txtColor}
                                           onChange={onInputChange}
                                           placeholder='Номер телефона'
                                           errorMessage='Укажите корректный номер телефона'
                                           className={'ym-disable-keys'}
                            />

                        <BaseButton
                            data-test={dataTestForButton}
                            text={'Подключить'}
                            onClick={onClickConnect}
                        />

                        {banner.link &&
                            <ST.DetailsLink
                                href={banner.link}
                                extStyles={extStyles?.phoneBlock}
                                target='_blank'
                                rel='nofollow noopener'
                                onClick={() => beforeClick(bannerIdx)}>
                                Подробнее
                            </ST.DetailsLink>
                            }
                            <ST.FootNote extStyles={extStyles?.phoneBlock}>Нажимая кнопку, вы даёте согласие на обработку&nbsp;
                                <ST.OfferLinkWrapper>
                                    <ST.OfferLink
                                        extStyles={extStyles?.phoneBlock}
                                        href='https://dom.ru/policy.pdf'
                                        target='_blank'
                                        rel='nofollow noopener'
                                    >
                                        персональных данных
                                    </ST.OfferLink>
                                </ST.OfferLinkWrapper>
                            </ST.FootNote>
                        </ST.PhoneBlock>
                        : null
                }
                {
                    (buttonText && !phoneIntroduction) &&
                    <ST.ButtonBlock>
                        {
                            typeButton === 'text'
                                ? <Link as={routeLink?.as} href={routeLink?.href || ''}>
                                    <ST.TextButton dangerouslySetInnerHTML={{ __html: buttonText }}/>
                                </Link>
                                : <BaseButton
                                    data-test={dataTestForButton}
                                    onClick={onBtnClick}
                                    type={btnTypes.ACCENT}
                                >
                                    <span dangerouslySetInnerHTML={{ __html: buttonText }} />
                                </BaseButton>
                        }
                    </ST.ButtonBlock>
                }
            </ST.Content>
            <ST.Images
                bgMobile={bgImageMobileWebp}
                adaptive={adaptive}
                position={frontImagePosition}
                marginRight={marginRight}
                styles={customCSSStyles?.image}
            >
                <BannerImage
                    url={resultFontImage}
                    urlWebp={frontImageWebp}
                    urlMobile={!frontImageMobileIsFallback ? frontImageMobile : undefined}
                    height={imageHeight}
                />
            </ST.Images>
        </>
    ), [
        bgImageMobileWebp,
        buttonText,
        descriptionText,
        frontImageMobile,
        frontImageMobileIsFallback,
        frontImagePosition,
        frontImageWebp,
    ]);

    return (
        <ST.Wrapper
            backgroundColor={backgroundColor}
            background={resultBgImage}
            backgroundMobile={resultMobileImage}
            marginTop={marginTop}
            marginBottom={marginBottom}
            fixedHeight={fixedHeight}
            customStyles={customStyles?.wrapper}
            adaptive={adaptive}
            {...props}
            itemScope
            itemType='http://schema.org/ImageObject'
            onClick={onBannerClick}
            styles={customCSSStyles?.wrapper}
        >
            {withContainer
                ? <ST.Container styles={customCSSStyles?.container}>{content}</ST.Container>
                : content
            }
        </ST.Wrapper>
    );
};

interface BannerImageProps {
    url?: string,
    urlWebp?: string,
    height?: string,
    urlMobile?: string,
}

const BannerImage = React.memo(function BannerImage({ url, urlWebp, urlMobile, height }: BannerImageProps) {
    if (!urlMobile && (!url && !urlWebp)) {
        return null;
    }
    return urlMobile
        ? <ST.FrontImageMobile
            width='607px'
            height='320px'
            alt='изображение баннера'
            itemProp='contentUrl'
            src={urlMobile}
        />
        : <WebpImage urlWebp={urlWebp}>
            <ST.FrontImageDesktop
                height={height}
                alt='изображение баннера'
                itemProp='contentUrl'
                src={url}
            />
        </WebpImage>;
});

export default Banner;
